"use client";

import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {TripRatingItem} from "@/types/booking";
import {FaStar} from "react-icons/fa";
import {FiCalendar, FiMapPin, FiStar, FiUser} from "react-icons/fi";
import {PiBusLight} from "react-icons/pi";

export type TripRatingCardMeta = {
  from: string;
  to: string;
  driverName: string;
  driverCode: string;
  routeCode: string;
};

type TripRatingDetailRow = {
  passenger: string;
  boardingPoint: string;
  dropPoint: string;
  rating: string;
  feedback: string;
  attachment: string;
};

export const buildTripMeta = (trip: TripRatingItem): TripRatingCardMeta => {
  const seed = Number(trip.id || 0);
  const routeStops = [
    ["KG Cinemas Stop", "Test Institute Branch 1"],
    ["Hope College", "NeoSay Campus"],
    ["Singanallur", "NeoSay"],
    ["RS Puram", "NeoSay"],
    ["Neelambur", "NeoSay"]
  ];
  const [from, to] = routeStops[seed % routeStops.length];

  return {
    from,
    to,
    driverName: ["John Driver", "Arun Driver", "Meena Driver", "Kumar Driver"][
      seed % 4
    ],
    driverCode: `SAMP${String(62 + seed).padStart(3, "0")}`,
    routeCode: trip.routeName || `Route-${String(seed + 1).padStart(2, "0")}`
  };
};

export const buildTripDetailRows = (
  trip: TripRatingItem
): TripRatingDetailRow[] => {
  const meta = buildTripMeta(trip);

  return Array.from({length: 6}, (_, index) => ({
    passenger:
      index === 0
        ? trip.name
        : `Passenger ${String.fromCharCode(65 + index)}${trip.id}`,
    boardingPoint: index % 2 === 0 ? meta.from : `${meta.from} Stop ${index}`,
    dropPoint: index % 2 === 0 ? meta.to : `${meta.to} Gate ${index}`,
    rating: `${Math.max(1, Math.min(5, trip.rating - (index % 2 ? 1 : 0)))}/5`,
    feedback:
      index === 0
        ? trip.description
        : "Trip feedback captured successfully for this passenger.",
    attachment: trip.filePath ? "Available" : "Not Available"
  }));
};

export const formatTripDate = (value: number) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric"
  }).format(new Date(value));

export const formatTripTime = (value: number) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(new Date(value));

export const TripRatingCard = ({
  trip,
  onViewDetail
}: {
  trip: TripRatingItem;
  onViewDetail: (trip: TripRatingItem) => void;
}) => {
  const meta = buildTripMeta(trip);
  const ratingCount = buildTripDetailRows(trip).length;
  const roundedRating = Math.max(0, Math.min(5, Math.round(trip.rating)));

  return (
    <div className="h-64 w-full min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="truncate font-bold leading-none tracking-tight text-slate-900">
              {trip.vehicleName}
            </div>
            <div className="mt-1 truncate text-xs font-medium text-slate-500">
              {meta.routeCode}
            </div>
          </div>

          <div className="shrink-0">
            <div className="mt-1 flex items-center justify-end gap-1 text-right">
              <span className="text-sm font-semibold text-slate-900">
                {trip.rating.toFixed(1)}
              </span>
              <div className="flex items-center gap-0.5 text-orange-500">
                {[0, 1, 2, 3, 4].map(index =>
                  index < roundedRating ? (
                    <FaStar key={index} className="h-3.5 w-3.5" />
                  ) : (
                    <FiStar key={index} className="h-3.5 w-3.5" />
                  )
                )}
              </div>
              <span className="text-sm font-medium text-slate-500">
                ({ratingCount})
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-[18px] bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-900">
          {formatTripTime(trip.time)} -{" "}
          {formatTripTime(trip.time + 90 * 60 * 1000)}
        </div>

        <div className="relative mt-1 space-y-2 pl-9">
          <div className="absolute left-[11px] top-3 h-[30px] w-0.5 border-l-2 border-dotted border-slate-200" />
          <div className="relative">
            <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
              <FiMapPin className="h-3 w-3 text-blue-500" />
            </span>
            <div className="truncate text-sm font-medium text-slate-900">
              {meta.from}
            </div>
          </div>
          <div className="relative">
            <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50">
              <FiMapPin className="h-3 w-3 text-emerald-500" />
            </span>
            <div className="truncate text-sm font-medium text-slate-900">
              {meta.to}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <FiCalendar className="h-3.5 w-3.5 text-slate-500" />
              <span>{formatTripDate(trip.time)}</span>
            </div>
            <div className="flex items-center gap-2">
              <FiUser className="h-3.5 w-3.5 text-slate-500" />
              <span>{meta.driverName}</span>
            </div>
            <div className="flex items-center gap-2">
              <PiBusLight className="h-3.5 w-3.5 text-slate-500" />
              <span>{meta.driverCode}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            className="rounded-[18px] border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
            onClick={() => onViewDetail(trip)}
            type="button"
          >
            View Detail
          </button>
        </div>
      </div>
    </div>
  );
};

export const TripRatingDetailView = ({
  trip,
  backHref,
  onClose
}: {
  trip: TripRatingItem;
  backHref?: string;
  onClose?: () => void;
}) => {
  const meta = buildTripMeta(trip);
  const rows = buildTripDetailRows(trip);
  const columns = [
    {uid: "passenger", name: "Passenger"},
    {uid: "boardingPoint", name: "Boarding Point"},
    {uid: "dropPoint", name: "Drop Point"},
    {uid: "rating", name: "Rating"},
    {uid: "feedback", name: "Feedback"},
    {uid: "attachment", name: "Attachment"}
  ];

  const detailTable = {
    columns,
    activeColumns: columns.map(column => column.uid),
    tabledatas: rows,
    style: {
      height: "h-[420px]"
    },
    meta: {
      id: 0,
      title: "",
      description: "",
      hasSearch: true,
      hasUpload: false,
      hasDownload: true,
      hasAdd: false,
      hasActions: false,
      hasCheckbox: false,
      hasFilter: false,
      hasHeader: true,
      hasFooter: false,
      hasPagination: false,
      hasCreate: false
    },
    url: {
      page: {},
      api: {
        get: ""
      }
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-63px)] flex-col px-5 py-5">
      <div className=" mb-2 text-lg  ">Trip Rating</div>

      <div className="flex items-start justify-between gap-3">
        <div className="flex w-full flex-row justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <div className="font-semibold text-slate-900">
              {trip.vehicleName}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Route :</div>
                <div className="font-semibold text-slate-900">
                  {meta.from} {"->"} {meta.to}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Route Name :</div>
                <div className="font-semibold text-slate-900">
                  {meta.routeCode}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Date :</div>
                <div className="font-semibold text-slate-900">
                  {formatTripDate(trip.time)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Trip Time :</div>
                <div className="font-semibold text-slate-900">
                  {formatTripTime(trip.time)} -{" "}
                  {formatTripTime(trip.time + 90 * 60 * 1000)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Driver :</div>
                <div className="font-semibold text-slate-900">
                  {meta.driverName}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="text-slate-600">Rating :</div>
                <div className="font-semibold text-slate-900">
                  {trip.rating}/5
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 h-[420px] w-full">
        <NeoAGgrid table={detailTable} />
      </div>
    </div>
  );
};
